/**
 * Zwergenwald - Passform-Check (blocks/zw-passform.liquid, O-93, Auftrag D-1).
 *
 * Ablauf wie bei mallie.de: eine Frage pro Bildschirm, ein Tipp geht weiter, "Zurueck" jederzeit,
 * kein Fruehabbruch. Erst nach Frage 6 wird ausgewertet (Runde 4: Alters- und Hoehenfrage raus,
 * Runde 7: auch die Frage "steigt das Kind allein aus dem Bett" raus):
 *   passt nicht  - mindestens ein Ausschluss (Bettart, Staerke)
 *   anschauen    - anderes Bett, zwei Einzelbetten nebeneinander, anderes Mass, oder Boxspringbett mit
 *                  140/160 cm (dort gibt es kein 3er-Set, und das Einzelgitter ist eher fuer Lattenrost
 *                  gedacht - ehrlicher Satz statt stiller Vorauswahl des Einzelgitters)
 *   passt        - Groesse 180/200 -> Set; 140/160 nur mit Lattenrost -> Einzelgitter
 *
 * Die Daten (Varianten, Preise, Warnsaetze, Texte) kommen als JSON aus Liquid, die Fragen stehen
 * als HTML im Block. Dieses Modul blendet nur um - wie ein useState-Schrittzaehler in React,
 * dessen Inhalte serverseitig gerendert sind. Antworten liegen in sessionStorage, damit ein
 * Neuladen (z. B. nach dem Kontaktformular) den Stand nicht verliert.
 *
 * Warenkorb (Inhaber, 05.10.2026): Das Ergebnis legt NICHTS mehr von selbst in den Warenkorb. Es waehlt
 * die passende Groesse nur in Horizons Kaufbox vor. Erst der Glut-Knopf "In den Warenkorb" im Ergebnis
 * "passt" legt die empfohlene Variante mit Menge 1 hinein - genau ein POST auf /cart/add.js pro Klick,
 * ueber dieselbe Event-Logik wie Horizons Kaufknopf (Warenkorb-Schublade, Zaehler). Vergleich React:
 * ein onClick-Handler statt eines useEffect, der beim Rendern feuert.
 *   - Keine Positions-Eigenschaft (line item property): Die Zeile ist eine ganz normale Warenkorbzeile,
 *     nichts davon wandert in Bestellung, OrderDesk oder easybill. Quizantworten bleiben im Browser.
 *   - Duplikate: Jeder Klick liest zuerst /cart.js. Liegt die empfohlene Variante schon im Warenkorb
 *     (egal woher), wird nichts gesendet - stattdessen "Liegt schon im Warenkorb" mit "Zum Warenkorb".
 *   - Erfolg = die Menge dieser Variante im Warenkorb ist nach dem POST groesser als davor (neu gelesen).
 *     Bei unklarem Ausgang (Netzfehler) wird nie blind wiederholt; der naechste Klick liest erst.
 *   - Anzeige: Beim Zeigen des Ergebnisses (auch nach Reload, Zurueck-Navigation oder einer Aenderung
 *     in der Warenkorb-Schublade) liest das Modul kurz /cart.js - nur lesen, nie hinzufuegen. Wurde
 *     die Variante inzwischen entfernt, erscheint der Knopf wieder. Wie ein useEffect, der nur fetcht.
 *   - Doppelklick: waehrend der Anfrage ignoriert der Knopf weitere Klicks (aria-disabled).
 * Die echte Variant-ID kommt aus Liquid; die separate Optionswert-ID bleibt fuer Horizon.
 *
 * B-Ware (data-kaufweg="auswahl", Inhaber 08.10.2026): derselbe Check, aber ohne jeden Warenkorb-Weg. Kein POST,
 * kein Lesen von /cart.js, kein Status "Liegt im Warenkorb". "Diese Groesse auswaehlen" waehlt die Groesse und
 * springt zur Kaufbox der B-Ware (zw-bware-zustand) - gekauft wird nur dort, mit Pflicht-Haekchen und
 * "Jetzt kaufen". Ohne data-kaufweg laeuft alles wie oben beschrieben (Neuware).
 */

import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const REIHENFOLGE = ['bett', 'groesse', 'staerke', 'bisher', 'wunsch', 'herkunft'];
const FRAGEN_GESAMT = REIHENFOLGE.length;
const SCHRITTE_GESAMT = FRAGEN_GESAMT + 1; // die Fragen und das Ergebnis
const LEER = Object.freeze({ variante: '', titel: '', status: '', meldung: '' });

/** Summe der Menge einer Variante ueber alle Warenkorbzeilen (/cart.js oder Antwort von /cart/add.js) */
const mengeIn = (items, variante) =>
  (Array.isArray(items) ? items : [])
    .filter((item) => item && String(item.variant_id ?? item.id) === variante)
    .reduce((summe, item) => summe + (Number(item.quantity) || 0), 0);

/**
 * Masse und Mengen nie trennen - geschuetzte Leerzeichen (Runde 3): "180 cm", "180 × 200",
 * "à 200 cm" und die Stueckzahl am Anfang eines Teils ("1 Gitter", auch nach " · ").
 */
const FEST = String.fromCharCode(160); // geschuetztes Leerzeichen
const masseZusammen = (text) =>
  text
    .replace(/(\d) (?=(?:cm|mm|kg)\b)/g, `$1${FEST}`)
    .replace(/(\d) × (?=\d)/g, `$1${FEST}×${FEST}`)
    .replace(/ à (?=\d)/g, ` à${FEST}`)
    .replace(/(^|· )(\d+) /g, `$1$2${FEST}`);

class ZwPassform extends HTMLElement {
  #daten = { varianten: {}, warn: {}, text: {} };
  #antworten = {};
  #verlauf = [];
  #freitexte = {};
  #offenerFreitext = '';
  #warenkorb = { ...LEER }; // Zustand des Knopfs fuer die gerade empfohlene Variante
  #laeuft = false; // eine Warenkorb-Anfrage (Klick) ist unterwegs
  #pruefNummer = 0; // verwirft veraltete Lese-Antworten
  #kennung = `zw-passform-${Math.random().toString(36).slice(2)}`; // erkennt eigene Warenkorb-Events
  #vorbelegung = ''; // zuletzt automatisch eingetragene Nachricht - nur diese darf der Check ueberschreiben
  #nurAuswahl = false; // B-Ware: das Ergebnis waehlt nur die Groesse, nie Warenkorb

  connectedCallback() {
    this.#nurAuswahl = this.dataset.kaufweg === 'auswahl';
    const quelle = this.querySelector('[data-zw-pf-daten]');
    try {
      if (quelle) this.#daten = JSON.parse(quelle.textContent || '{}');
    } catch (fehler) {
      console.warn('Passform-Check: Daten nicht lesbar', fehler);
    }

    this.addEventListener('click', this.#beiKlick);
    this.addEventListener('input', this.#beiEingabe);
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#beiWarenkorbAenderung);
    window.addEventListener('pageshow', this.#beiSeitenRueckkehr);
    this.#ankerEindeutig();
    this.#laden();

    const formular = this.querySelector('form');
    const gesendetHier = !location.hash || (formular?.id && location.hash === `#${formular.id}`);
    if (this.querySelector('[data-zw-pf-gesendet]') && gesendetHier) {
      this.#zeigeKontakt(true);
    }
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#beiKlick);
    this.removeEventListener('input', this.#beiEingabe);
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#beiWarenkorbAenderung);
    window.removeEventListener('pageshow', this.#beiSeitenRueckkehr);
  }

  /** Steht derselbe Anker auf zwei Checks, behaelt nur der erste die ID. */
  #ankerEindeutig() {
    const abschnitt = this.closest('.zw-passform');
    if (!(abschnitt instanceof HTMLElement) || !abschnitt.id) return;
    const erster = document.getElementById(abschnitt.id);
    if (erster && erster !== abschnitt) abschnitt.removeAttribute('id');
  }

  /** @param {MouseEvent} event */
  #beiKlick = (event) => {
    const ziel = event.target instanceof Element ? event.target : null;
    if (!ziel) return;

    if (ziel.closest('[data-zw-pf-zurueck]')) return this.#zurueck();
    if (ziel.closest('[data-zw-pf-neu]')) return this.#neu();
    if (ziel.closest('[data-zw-pf-frage]')) return this.#zeigeKontakt(false);
    if (ziel.closest('[data-zw-pf-kopieren]')) return this.#kopieren(ziel.closest('[data-zw-pf-kopieren]'));
    if (ziel.closest('[data-zw-pf-warenkorb]')) {
      event.preventDefault();
      void this.#warenkorbHinzufuegen();
      return;
    }
    const waehlen = ziel.closest('[data-zw-waehle-wert]');
    if (waehlen instanceof HTMLElement && this.#nurAuswahl) {
      // B-Ware: Groesse waehlen, dann zur Kaufbox mit dem Haekchen. stopPropagation, damit zw-variantenkarten.js
      // nicht zusaetzlich zur Groessenwahl springt.
      event.preventDefault();
      event.stopPropagation();
      this.#waehleStill(waehlen.dataset.zwWaehleWert || '');
      this.#zurKaufbox();
      return;
    }
    if (waehlen instanceof HTMLElement && !customElements.get('zw-variantenkarten')) {
      event.preventDefault();
      this.#waehleStill(waehlen.dataset.zwWaehleWert || '');
      const picker = document.querySelector('variant-picker');
      picker?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    const weiter = ziel.closest('[data-zw-pf-weiter]');
    if (weiter instanceof HTMLElement) {
      const schritt = weiter.closest('[data-schritt]');
      if (!(schritt instanceof HTMLElement) || schritt.hidden) return;
      const name = schritt.dataset.schritt || '';
      const feld = schritt.querySelector('[data-zw-pf-freitext]');
      this.#freitexte[name] = feld instanceof HTMLInputElement ? feld.value.slice(0, 120) : '';
      if (name === 'bisher' && !weiter.hasAttribute('data-zw-pf-kontakt-weiter')) {
        this.#speichern();
        this.#bisherKontakt(schritt);
        weiter.hidden = true;
        return;
      }
      return this.#antworten_setzen(name, name === 'herkunft' ? 'woanders' : 'anderes');
    }

    const knopf = ziel.closest('[data-antwort]');
    if (!(knopf instanceof HTMLElement)) return;
    const schritt = knopf.closest('[data-schritt]');
    if (!(schritt instanceof HTMLElement) || schritt.hidden) return;

    if (knopf.hasAttribute('data-freitext')) {
      const feld = schritt.querySelector('[data-zw-pf-freitext-feld]');
      if (feld instanceof HTMLElement) {
        this.#offenerFreitext = schritt.dataset.schritt || '';
        this.#speichern();
        feld.hidden = false;
        feld.querySelector('input')?.focus();
      }
      this.#markiere(schritt, knopf);
      return;
    }

    this.#antworten_setzen(schritt.dataset.schritt || '', knopf.dataset.antwort || '');
  };

  /** @param {string} schritt @param {string} wert */
  #antworten_setzen(schritt, wert) {
    if (!schritt) return;
    this.#offenerFreitext = '';
    this.#antworten[schritt] = wert;
    if (wert !== 'anderes' && wert !== 'woanders') this.#freitexte[schritt] = '';
    this.#verlauf.push(schritt);
    this.#speichern();
    this.#zeige(this.#naechster(schritt));
  }

  #beiEingabe = (event) => {
    const feld = event.target;
    if (!(feld instanceof HTMLInputElement) || !feld.hasAttribute('data-zw-pf-freitext')) return;
    const schritt = feld.closest('[data-schritt]');
    if (!(schritt instanceof HTMLElement) || schritt.hidden) return;
    this.#freitexte[schritt.dataset.schritt || ''] = feld.value.slice(0, 120);
    this.#speichern();
    const angebot = schritt.querySelector('[data-zw-pf-kontakt-angebot]');
    if (angebot instanceof HTMLElement && !angebot.hidden) this.#bisherKontakt(schritt, { fokus: false });
  };

  /** @param {string} aktuell */
  #naechster(aktuell) {
    const index = REIHENFOLGE.indexOf(aktuell) + 1;
    return index > 0 && index < REIHENFOLGE.length ? REIHENFOLGE[index] : 'ergebnis';
  }

  #zurueck() {
    const vorher = this.#verlauf.pop();
    if (!vorher) return;
    this.#speichern();
    this.#zeige(vorher);
  }

  #neu() {
    if (this.#laeuft) return;
    this.#antworten = {};
    this.#verlauf = [];
    this.#freitexte = {};
    this.#offenerFreitext = '';
    this.#warenkorb = { ...LEER };
    this.#pruefNummer++;
    this.#speichern();
    const nachricht = this.querySelector('[data-zw-pf-nachricht]');
    if (nachricht instanceof HTMLTextAreaElement && nachricht.value === this.#vorbelegung) nachricht.value = '';
    this.#vorbelegung = '';
    this.querySelectorAll('[data-zw-pf-freitext]').forEach((feld) => {
      if (feld instanceof HTMLInputElement) feld.value = '';
    });
    this.#zeige('bett');
  }

  /** @param {string} name */
  #zeige(name, { fokus = true } = {}) {
    const ergebnisArt = name === 'ergebnis' ? this.#auswerten() : '';
    this.querySelectorAll('[data-schritt], [data-ergebnis]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const sichtbar = name === 'ergebnis' ? el.dataset.ergebnis === ergebnisArt : el.dataset.schritt === name;
      if (el.hidden === sichtbar) el.hidden = !sichtbar;
    });
    const kontakt = this.querySelector('[data-zw-pf-kontakt]');
    if (kontakt instanceof HTMLElement && name !== 'ergebnis') kontakt.hidden = true;

    let anzeige;
    let nummer;
    if (name === 'ergebnis') {
      const art = ergebnisArt;
      anzeige = this.querySelector(`[data-ergebnis="${art}"]`);
      nummer = SCHRITTE_GESAMT;
      this.#fuelleErgebnis(art, { waehlen: fokus });
      this.#zaehler('Fertig', SCHRITTE_GESAMT);
    } else {
      anzeige = this.querySelector(`[data-schritt="${CSS.escape(name)}"]`);
      nummer = Number(anzeige?.dataset.nummer || 1);
      this.#zaehler(`Frage ${nummer} von ${FRAGEN_GESAMT}`, nummer);
      if (anzeige instanceof HTMLElement) this.#markiere(anzeige, null);
      if (anzeige instanceof HTMLElement) {
        const feld = anzeige.querySelector('[data-zw-pf-freitext-feld]');
        if (feld instanceof HTMLElement) feld.hidden = name !== this.#offenerFreitext && !['anderes', 'woanders'].includes(this.#antworten[name]);
        const eingabe = anzeige.querySelector('[data-zw-pf-freitext]');
        const entwurf = this.#freitexte[name] || '';
        if (eingabe instanceof HTMLInputElement && eingabe.value !== entwurf) eingabe.value = entwurf;
        const angebot = anzeige.querySelector('[data-zw-pf-kontakt-angebot]');
        if (angebot instanceof HTMLElement) angebot.hidden = true;
        const weiter = anzeige.querySelector('[data-zw-pf-weiter]');
        if (weiter instanceof HTMLElement) weiter.hidden = false;
      }
    }
    if (!(anzeige instanceof HTMLElement)) return;

    anzeige.hidden = false;
    const zurueck = this.querySelector('[data-zw-pf-zurueck]');
    if (zurueck instanceof HTMLElement) zurueck.hidden = this.#verlauf.length === 0;

    const titel = anzeige.querySelector('.zw-passform__frage');
    const ansage = this.querySelector('[data-zw-pf-ansage]');
    if (ansage) ansage.textContent = `${name === 'ergebnis' ? 'Ergebnis' : `Frage ${nummer} von ${FRAGEN_GESAMT}`}: ${titel?.textContent?.trim() ?? ''}`;
    if (fokus && titel instanceof HTMLElement) titel.focus({ preventScroll: true });
    if (fokus && this.getBoundingClientRect().top < 0) this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    this.#warenkorbStatus();
    if (name === 'ergebnis') void this.#warenkorbPruefen(); // nur lesen - fuegt nie hinzu
  }

  /** @param {string} text @param {number} schritt */
  #zaehler(text, schritt) {
    const zaehler = this.querySelector('[data-zw-pf-zaehler]');
    if (zaehler) zaehler.textContent = text;
    const balken = this.querySelector('[role="progressbar"]');
    balken?.setAttribute('aria-valuenow', String(schritt));
    balken?.setAttribute('aria-valuetext', text);
    const fuellung = this.querySelector('[data-zw-pf-balken]');
    if (fuellung instanceof HTMLElement) fuellung.style.width = `${Math.round((schritt / SCHRITTE_GESAMT) * 100)}%`;
  }

  /** @param {HTMLElement} schritt @param {Element | null} gewaehlt */
  #markiere(schritt, gewaehlt) {
    const name = schritt.dataset.schritt;
    const wert = name ? this.#antworten[name] : undefined;
    schritt.querySelectorAll('[data-antwort]').forEach((knopf) => {
      const an = gewaehlt ? knopf === gewaehlt : knopf instanceof HTMLElement && (name === this.#offenerFreitext ? knopf.hasAttribute('data-freitext') : knopf.dataset.antwort === wert);
      knopf.setAttribute('aria-pressed', an ? 'true' : 'false');
    });
  }

  #gruende() {
    const a = this.#antworten;
    const gruende = [];
    if (a.bett === 'ungeeignet') gruende.push('bett');
    if (a.staerke === 'dick') gruende.push('matratze');
    return gruende;
  }

  #auswerten() {
    if (!this.#vollstaendig()) return 'anschauen';
    if (this.#gruende().length) return 'passt-nicht';
    // Zwei Einzelbetten nebeneinander: dass Set oder Einzelgitter an zwei getrennten Matratzen halten,
    // ist nicht belegt (Pruefung D-3) - deshalb gemeinsam anschauen statt "passt".
    if (this.#antworten.bett === 'anderes' || this.#antworten.bett === 'einzelbetten' || this.#antworten.groesse === 'anderes') return 'anschauen';
    // Runde 7: Bei 140/160 cm gibt es kein 3er-Set, und das Einzelgitter ist eher fuer Lattenrost gedacht.
    // Freigabeliste statt Sperrliste: nur mit "Lattenrost" wird das Einzelgitter vorgeschlagen, bei jeder
    // anderen Bettart (heute Boxspring, auch eine kuenftig neue) gilt gemeinsam anschauen (Kontakt) - keine
    // stille Vorauswahl des Einzelgitters.
    if (this.#schmal() && this.#antworten.bett !== 'lattenrost') return 'anschauen';
    // B-Ware: fehlt die Groesse in den Daten (SKU nicht gefunden), kein leeres "passt", sondern gemeinsam anschauen
    if (this.#nurAuswahl && !this.#daten.varianten?.[this.#variantenSchluessel()]) return 'anschauen';
    return this.#variantenSchluessel() ? 'passt' : 'anschauen';
  }

  /** Matratzenbreite 140 oder 160 cm: dort gibt es kein 3er-Set */
  #schmal() {
    return this.#antworten.groesse === '140' || this.#antworten.groesse === '160';
  }

  #variantenSchluessel() {
    const g = this.#antworten.groesse;
    if (g === '180') return 'set180';
    if (g === '200') return 'set200';
    if (g === '140' || g === '160') return 'einzel';
    return '';
  }

  /** @param {string} art */
  #fuelleErgebnis(art, { waehlen = true } = {}) {
    const { varianten = {}, warn = {}, text = {} } = this.#daten;

    if (art === 'passt') {
      const schluessel = this.#variantenSchluessel();
      const v = varianten[schluessel] || {};
      const satz = (text.satzPasst || '')
        .replace('[bett]', this.#satzteil('bett') || 'euer Bett')
        .replace('[groesse]', this.#satzteil('groesse'))
        .replace('[variante]', v.titel || '');
      this.#setzeText('[data-zw-pf-satz]', satz);

      const wunsch = this.querySelector(`[data-schritt="wunsch"] [data-antwort="${CSS.escape(this.#antworten.wunsch || '')}"]`);
      let persoenlich = '';
      if (wunsch instanceof HTMLElement) {
        persoenlich = (schluessel === 'einzel' && wunsch.dataset.persoenlichEinzel) || wunsch.dataset.persoenlich || '';
      }
      this.#setzeText('[data-zw-pf-persoenlich]', persoenlich);

      this.#chips('[data-zw-pf-chips]');
      this.#setzeText('[data-zw-pf-v-titel]', v.titel || '');
      this.querySelectorAll('[data-zw-pf-v-bild]').forEach((bild) => {
        bild.toggleAttribute('hidden', bild.getAttribute('data-zw-pf-v-bild') !== schluessel);
      });
      this.#setzeText('[data-zw-pf-v-umfang]', v.lieferumfang || '');
      this.#setzeText('[data-zw-pf-v-preis]', v.preis || '');
      // B-Ware: nie ein Set-Vorteil (er rechnet mit Neuware-Preisen) - Liquid liefert ihn dort ohnehin leer
      this.#setzeText('[data-zw-pf-v-zusatz]', schluessel === 'einzel' ? text.satzEinzel || '' : (this.#nurAuswahl ? '' : v.vorteil) || '');
      this.#setzeText('[data-zw-pf-nur-set]', this.#schmal() ? text.satzNurSet || '' : '');

      const auswahl = this.querySelector('[data-zw-pf-auswaehlen]');
      if (auswahl instanceof HTMLElement) {
        if (v.wert && v.bestellbar !== false) {
          auswahl.dataset.zwWaehleWert = String(v.wert);
          auswahl.hidden = false;
        } else {
          delete auswahl.dataset.zwWaehleWert;
          auswahl.hidden = true;
        }
      }
      // B-Ware: der Satz "Gekauft wird in der Kaufbox ..." steht nur, solange es etwas auszuwaehlen gibt
      const auswahlHinweis = this.querySelector('[data-zw-pf-auswahl-hinweis]');
      if (auswahlHinweis instanceof HTMLElement) auswahlHinweis.hidden = !(auswahl instanceof HTMLElement) || auswahl.hidden;

      const einzelLink = this.querySelector('[data-zw-pf-einzel-link]');
      const einzel = varianten.einzel;
      if (einzelLink instanceof HTMLElement) {
        // Runde 7: das Einzelgitter ist eher fuer Lattenrost - der Link erscheint nur nach "Lattenrost"
        const zeigen = schluessel !== 'einzel' && this.#antworten.bett === 'lattenrost' && einzel && einzel.wert && einzel.bestellbar !== false;
        einzelLink.hidden = !zeigen;
        if (zeigen) einzelLink.dataset.zwWaehleWert = String(einzel.wert);
      }

      const liste = this.querySelector('[data-zw-pf-aufbau]');
      if (liste) {
        liste.replaceChildren();
        [warn.hoehe, warn.klettern, warn.sitz, text.satzTopper].filter(Boolean).forEach((satz) => {
          const li = document.createElement('li');
          li.textContent = masseZusammen(satz);
          liste.append(li);
        });
      }
      const alle = this.querySelector('[data-zw-pf-alle-warn]');
      if (alle instanceof HTMLAnchorElement && this.#daten.warnhinweiseAnker) {
        alle.href = `#${this.#daten.warnhinweiseAnker}`;
      }

      // Nur vorwaehlen, nie hinzufuegen: das macht allein der Knopf "In den Warenkorb" (05.10.2026)
      if (waehlen && v.wert && v.bestellbar !== false) {
        this.#waehleStill(String(v.wert));
      }
    }

    if (art === 'passt-nicht') {
      const liste = this.querySelector('[data-zw-pf-gruende]');
      if (liste) {
        liste.replaceChildren();
        this.#gruende().forEach((grund) => {
          const li = document.createElement('li');
          li.textContent = masseZusammen(warn[grund] || text.ersatzGrund || '');
          liste.append(li);
        });
      }
    }

    if (art === 'anschauen') {
      const boxspringSchmal = this.#antworten.bett === 'boxspring' && this.#schmal();
      this.#setzeText('[data-zw-pf-anschauen-boxspring]', boxspringSchmal ? text.satzBoxspringEinzel || '' : '');
      this.#chips('[data-zw-pf-chips-anschauen]');
      this.#zeigeKontakt(false, { fokus: false });
    }
  }

  /** @param {string} schritt */
  #satzteil(schritt) {
    const knopf = this.querySelector(`[data-schritt="${schritt}"] [data-antwort="${CSS.escape(this.#antworten[schritt] || '')}"]`);
    return knopf instanceof HTMLElement ? knopf.dataset.satzteil || knopf.textContent?.trim() || '' : '';
  }

  #antwortTexte() {
    const zeilen = [];
    for (const schritt of REIHENFOLGE) {
      const wert = this.#antworten[schritt];
      if (!wert) continue;
      const frage = this.querySelector(`[data-schritt="${schritt}"] .zw-passform__frage`)?.textContent?.trim() ?? schritt;
      const knopf = this.querySelector(`[data-schritt="${schritt}"] [data-antwort="${CSS.escape(wert)}"]`);
      let antwort = knopf?.textContent?.trim() ?? wert;
      if (['anderes', 'woanders'].includes(wert) && this.#freitexte[schritt]) antwort += ` (${this.#freitexte[schritt]})`;
      const chip = knopf instanceof HTMLElement && knopf.dataset.chip ? knopf.dataset.chip : antwort;
      zeilen.push({ frage, antwort, chip, schritt });
    }
    return zeilen;
  }

  /** @param {string} selektor */
  #chips(selektor) {
    const liste = this.querySelector(selektor);
    if (!liste) return;
    liste.replaceChildren();
    this.#antwortTexte()
      .filter((z) => ['bett', 'groesse', 'staerke'].includes(z.schritt))
      .forEach((z) => {
        const li = document.createElement('li');
        li.textContent = masseZusammen(z.chip);
        liste.append(li);
      });
  }

  /** @param {boolean} gesendet */
  #zeigeKontakt(gesendet, { fokus = true } = {}) {
    const kontakt = this.querySelector('[data-zw-pf-kontakt]');
    if (!(kontakt instanceof HTMLElement)) return;
    kontakt.hidden = false;
    if (gesendet) return;
    const feld = kontakt.querySelector('[data-zw-pf-nachricht]');
    // Leer oder noch die eigene Vorbelegung: mit den aktuellen Antworten neu fuellen (wer zurueckgeht und
    // eine Antwort aendert, soll keine veralteten Zeilen verschicken). Hat der Besucher etwas geschrieben,
    // bleibt sein Text stehen.
    if (feld instanceof HTMLTextAreaElement && (!feld.value || feld.value === this.#vorbelegung)) {
      const anfang = this.#daten.text?.nachrichtAnfang || '';
      const zeilen = this.#antwortTexte().map((z) => `- ${z.frage} ${z.antwort}`);
      this.#vorbelegung = [anfang, ...zeilen, '', ''].join('\n');
      feld.value = this.#vorbelegung;
    }
    if (fokus) kontakt.querySelector('input[type="email"]')?.focus();
  }

  /** @param {Element | null} knopf */
  async #kopieren(knopf) {
    const code = this.querySelector('[data-zw-pf-code]')?.textContent?.trim();
    if (!code || !(knopf instanceof HTMLElement)) return;
    try {
      await navigator.clipboard.writeText(code);
      const alt = knopf.textContent;
      knopf.textContent = knopf.dataset.kopiert || alt;
      setTimeout(() => (knopf.textContent = alt), 2000);
    } catch {
      const bereich = document.createRange();
      const el = this.querySelector('[data-zw-pf-code]');
      if (el) {
        bereich.selectNodeContents(el);
        const auswahl = getSelection();
        auswahl?.removeAllRanges();
        auswahl?.addRange(bereich);
      }
    }
  }

  /** B-Ware: zur Kaufbox mit Pflicht-Haekchen und "Jetzt kaufen"; Fokus aufs Haekchen (setzt es nicht). */
  #zurKaufbox() {
    const kaufbox = document.querySelector('zw-bware-zustand') ?? document.querySelector('zw-variantenkarten') ?? document.querySelector('variant-picker');
    if (!(kaufbox instanceof HTMLElement)) return;
    kaufbox.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const haken = kaufbox.querySelector('[data-zw-haken]');
    if (haken instanceof HTMLElement) haken.focus({ preventScroll: true });
  }

  /** Waehlt die Variante in Horizons Auswahl, ohne zu scrollen. @param {string} id */
  #waehleStill(id) {
    const auswahl = `variant-picker input[type="radio"][data-option-value-id="${CSS.escape(id)}"]`;
    const ziel = document.querySelector(auswahl);
    if (ziel instanceof HTMLInputElement && !ziel.checked) ziel.click();
  }

  #vollstaendig() {
    return REIHENFOLGE.every((schritt) => {
      const wert = this.#antworten[schritt];
      return typeof wert === 'string' && this.#verlauf.includes(schritt) &&
        this.querySelector(`[data-schritt="${schritt}"] [data-antwort="${CSS.escape(wert)}"]`);
    });
  }

  #bisherKontakt(schritt, { fokus = true } = {}) {
    const angebot = schritt.querySelector('[data-zw-pf-kontakt-angebot]');
    if (!(angebot instanceof HTMLElement)) return;
    const nachricht = `Hallo Zwergenwald, wir haben eine Frage zu unserer bisherigen Lösung: ${this.#freitexte.bisher || 'Etwas anderes'}`;
    const email = angebot.querySelector('[data-zw-pf-loesung-email]');
    if (email instanceof HTMLAnchorElement) {
      email.href = `mailto:support@meinzwergenwald.de?subject=${encodeURIComponent('Frage aus dem Passform-Check')}&body=${encodeURIComponent(nachricht)}`;
    }
    const whatsapp = angebot.querySelector('a[href^="https://wa.me/"]');
    if (whatsapp instanceof HTMLAnchorElement) {
      const url = new URL(whatsapp.href);
      url.searchParams.set('text', nachricht);
      whatsapp.href = url.toString();
    }
    angebot.hidden = false;
    if (fokus) angebot.querySelector('a')?.focus();
  }

  /** Warenkorb anderswo geaendert (Schublade, Kaufknopf, zweiter Check): neu lesen, nie hinzufuegen */
  #beiWarenkorbAenderung = (event) => {
    event.promise
      ?.then((ergebnis) => {
        if (ergebnis?.detail?.sourceId === this.#kennung) return; // eigene Anfrage - den Zustand setzt der Klick
        void this.#warenkorbPruefen();
      })
      .catch(() => {});
  };

  /** Zurueck-Taste aus dem Warenkorb (Seite aus dem bfcache): neu lesen @param {PageTransitionEvent} event */
  #beiSeitenRueckkehr = (event) => {
    if (event.persisted) void this.#warenkorbPruefen();
  };

  /** Die Empfehlung des Ergebnisses "passt" - oder null, wenn gerade kein "passt" vorliegt */
  #empfehlung() {
    if (this.#auswerten() !== 'passt') return null;
    return this.#daten.varianten?.[this.#variantenSchluessel()] || null;
  }

  /** Echte Variant-ID der Empfehlung, wenn sie bestellbar ist - sonst '' */
  #bestellbareId(v) {
    const id = String(v?.id || '');
    return v && v.bestellbar !== false && /^\d+$/.test(id) ? id : '';
  }

  #ergebnisSichtbar() {
    const ergebnis = this.querySelector('[data-ergebnis="passt"]');
    return ergebnis instanceof HTMLElement && !ergebnis.hidden;
  }

  #root() {
    return globalThis.Shopify?.routes?.root || '/';
  }

  /** Liest /cart.js. Liefert die Menge der Variante und den ganzen Warenkorb. @param {string} variante */
  async #lesen(variante, wartezeit = 12000) {
    const gelesen = await this.#cartJson(`${this.#root()}cart.js`, {}, wartezeit);
    if (!gelesen.ok || !gelesen.daten || !Array.isArray(gelesen.daten.items)) throw new Error('Warenkorb konnte nicht gelesen werden');
    return { menge: mengeIn(gelesen.daten.items, variante), cart: gelesen.daten };
  }

  /**
   * Beim Anzeigen des Ergebnisses "passt" (und nach Warenkorb-Aenderungen): kurz /cart.js lesen.
   * Liegt die Variante drin, verschwindet der Knopf; wurde sie entfernt, erscheint er wieder.
   * Nur ein GET - diese Funktion fuegt nie etwas hinzu.
   */
  async #warenkorbPruefen() {
    if (this.#nurAuswahl || this.#laeuft || !this.#ergebnisSichtbar()) return;
    const v = this.#empfehlung();
    const variante = this.#bestellbareId(v);
    if (!variante) return;
    const nummer = ++this.#pruefNummer;
    const zuvor = this.#warenkorb.variante === variante ? this.#warenkorb.status : '';
    this.#warenkorb = { variante, titel: v.titel || '', status: 'pruefen', meldung: '' };
    this.#warenkorbStatus();
    let menge = null;
    try {
      menge = (await this.#lesen(variante, 6000)).menge;
    } catch {
      menge = null; // nicht lesbar: Knopf zeigen - ein Klick liest ohnehin zuerst selbst
    }
    if (nummer !== this.#pruefNummer || this.#laeuft || this.#warenkorb.variante !== variante) return;
    this.#warenkorb.status = menge !== null && menge > 0 ? (zuvor === 'done' ? 'done' : 'drin') : '';
    this.#warenkorbStatus();
  }

  #warenkorbStatus() {
    const status = this.querySelector('[data-zw-pf-warenkorb-status]');
    const knopf = this.querySelector('[data-zw-pf-warenkorb]');
    const link = this.querySelector('[data-zw-pf-warenkorb-link]');
    if (this.#nurAuswahl) {
      // B-Ware: kein Warenkorb-Zustand. Nur ehrlich sagen, wenn die empfohlene Groesse als B-Ware ausverkauft ist.
      const empfohlen = this.#empfehlung();
      const text = empfohlen && empfohlen.bestellbar === false ? 'Diese Größe gibt es gerade nicht als B-Ware.' : '';
      if (status instanceof HTMLElement && status.textContent !== text) status.textContent = text;
      if (knopf instanceof HTMLElement) knopf.hidden = true;
      if (link instanceof HTMLElement) link.hidden = true;
      return;
    }
    const v = this.#empfehlung();
    const variante = this.#bestellbareId(v);
    const bestellbar = Boolean(variante);
    const zustand = bestellbar && this.#warenkorb.variante === variante ? this.#warenkorb : null;
    let text = '';
    if (v && v.bestellbar === false) text = 'Diese Größe ist gerade nicht verfügbar.';
    else if (v && !bestellbar) text = 'Diese Größe lässt sich hier gerade nicht in den Warenkorb legen. Nutzt bitte den Kaufknopf oben am Produkt.';
    if (zustand?.status === 'pending') text = 'Wird in euren Warenkorb gelegt …';
    if (zustand?.status === 'done') text = `Liegt im Warenkorb: ${zustand.titel || 'die empfohlene Größe'}.`;
    if (zustand?.status === 'drin') text = 'Liegt schon im Warenkorb.';
    if (zustand?.status === 'failed') {
      text = `Das hat leider nicht geklappt.${zustand.meldung ? ` ${zustand.meldung}` : ''} Versucht es bitte noch einmal oder nutzt den Kaufknopf oben am Produkt.`;
    }
    if (zustand?.status === 'unknown') {
      text = 'Ob die Größe im Warenkorb liegt, konnten wir nicht bestätigen. Schaut bitte in euren Warenkorb – ein erneuter Klick prüft das zuerst und legt nichts doppelt hinein.';
    }
    // Die Live-Region bleibt im DOM (leer statt hidden), damit Screenreader jede Aenderung ansagen
    if (status instanceof HTMLElement && status.textContent !== text) status.textContent = text;
    const wartet = zustand?.status === 'pending';
    if (knopf instanceof HTMLButtonElement) {
      knopf.hidden = !bestellbar || ['pruefen', 'done', 'drin'].includes(zustand?.status || '');
      knopf.setAttribute('aria-disabled', wartet ? 'true' : 'false');
    }
    if (link instanceof HTMLElement) link.hidden = !zustand || !['done', 'drin', 'unknown'].includes(zustand.status);
    this.setAttribute('aria-busy', wartet ? 'true' : 'false');
    this.querySelectorAll('[data-zw-pf-zurueck], [data-zw-pf-neu]').forEach((el) => {
      if (el instanceof HTMLButtonElement) el.disabled = wartet;
    });
  }

  /** Verschwindet der Knopf (hinzugefuegt oder lag schon drin), geht der Fokus auf "Zum Warenkorb" - ausser die Schublade hat ihn */
  #fokusNachWarenkorb(zustand) {
    if (!['done', 'drin'].includes(zustand.status)) return;
    const aktiv = document.activeElement;
    const knopf = this.querySelector('[data-zw-pf-warenkorb]');
    if (aktiv && aktiv !== document.body && aktiv !== knopf) return;
    const link = this.querySelector('[data-zw-pf-warenkorb-link]');
    if (link instanceof HTMLElement && !link.hidden) link.focus();
  }

  async #cartJson(url, options = {}, wartezeit = 12000) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), wartezeit);
    try {
      const antwort = await fetch(url, { ...options, signal: controller.signal, credentials: 'same-origin', cache: 'no-store' });
      const daten = await antwort.json();
      return { ok: antwort.ok, daten };
    } finally { clearTimeout(timer); }
  }

  /**
   * Nur vom Klick auf "In den Warenkorb" aufgerufen - nie beim Anzeigen, Laden oder Zurueckgehen.
   * Legt die aktuelle Empfehlung mit Menge 1 in den Warenkorb: genau ein POST auf /cart/add.js.
   */
  async #warenkorbHinzufuegen() {
    if (this.#nurAuswahl) return; // B-Ware: nie in den Warenkorb, gekauft wird nur ueber "Jetzt kaufen"
    if (this.#laeuft) return; // Doppelklick: die erste Anfrage laeuft noch
    const v = this.#empfehlung();
    const variante = this.#bestellbareId(v);
    if (!variante) return;
    const alt = this.#warenkorb.variante === variante ? this.#warenkorb.status : '';
    if (['pending', 'pruefen', 'done', 'drin'].includes(alt)) return; // laeuft bzw. liegt schon drin
    this.#laeuft = true;
    this.#pruefNummer++; // eine noch laufende Anzeige-Pruefung zaehlt nicht mehr
    const zustand = { variante, titel: v.titel || '', status: 'pending', meldung: '' };
    this.#warenkorb = zustand;
    this.#warenkorbStatus();
    try {
      // Erst nachsehen: liegt die Variante schon im Warenkorb, wird nichts gesendet
      let vorher;
      try {
        vorher = (await this.#lesen(variante)).menge;
      } catch {
        zustand.status = 'failed'; // nichts gesendet - ein neuer Klick ist gefahrlos
        return;
      }
      if (vorher > 0) {
        zustand.status = 'drin';
        return;
      }
      await this.#senden(zustand, vorher);
    } finally {
      this.#laeuft = false;
      this.#warenkorbStatus();
      this.#fokusNachWarenkorb(zustand);
    }
  }

  /**
   * Der eigentliche POST - dieselbe Ereignis-Kette wie Horizons Kaufknopf (Schublade, Zaehler).
   * Ohne properties: eine ganz normale Warenkorbzeile. Erfolg = Menge danach groesser als davor.
   * @param {{variante: string, status: string, meldung: string}} zustand @param {number} vorher
   */
  async #senden(zustand, vorher) {
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const aufnahme = CartLinesUpdateEvent.createPromise();
    aufnahme.promise.catch(() => {});
    this.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: zustand.variante, quantity: 1 }], promise: aufnahme.promise }));
    let sectionHtml = {};
    let abgelehnt = false;
    let mengeLautAntwort = null;
    let postFehler = null;
    try {
      const antwort = await this.#cartJson(`${this.#root()}cart/add.js`, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: Number(zustand.variante), quantity: 1 }], sections, sections_url: location.pathname }),
      });
      abgelehnt = !antwort.ok;
      if (abgelehnt && typeof antwort.daten?.description === 'string') zustand.meldung = antwort.daten.description.slice(0, 200);
      sectionHtml = antwort.daten?.sections || {};
      if (antwort.ok) mengeLautAntwort = mengeIn(antwort.daten?.items || [antwort.daten], zustand.variante);
    } catch (fehler) {
      postFehler = fehler; // Die Antwort ging verloren - der POST kann trotzdem angekommen sein
    }
    // Nur lesen, niemals blind wiederholen: der Warenkorb danach entscheidet
    try {
      const { menge, cart } = await this.#lesen(zustand.variante);
      zustand.status = menge > vorher ? 'done' : abgelehnt ? 'failed' : 'unknown';
      aufnahme.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { source: 'zw-passform', sourceId: this.#kennung, sections: sectionHtml, items: cart.items, itemCount: cart.item_count, didError: zustand.status !== 'done' },
      });
    } catch (fehler) {
      zustand.status = mengeLautAntwort !== null && mengeLautAntwort > vorher ? 'done' : abgelehnt ? 'failed' : 'unknown';
      aufnahme.reject(postFehler || fehler);
    }
  }

  /** @param {string} selektor @param {string} text */
  #setzeText(selektor, text) {
    const el = this.querySelector(selektor);
    if (!(el instanceof HTMLElement)) return;
    el.textContent = masseZusammen(text);
    el.hidden = !text;
  }

  #speichern() {
    try {
      sessionStorage.setItem(
        this.dataset.speicher || 'zw-passform',
        JSON.stringify({ antworten: this.#antworten, verlauf: this.#verlauf, freitexte: this.#freitexte, offenerFreitext: this.#offenerFreitext })
      );
    } catch {
      /* Speicher gesperrt - der Check laeuft trotzdem, nur ohne Wiederherstellen */
    }
  }

  #laden() {
    let stand = null;
    try {
      stand = JSON.parse(sessionStorage.getItem(this.dataset.speicher || 'zw-passform') || 'null');
    } catch {
      stand = null;
    }
    if (stand && typeof stand === 'object' && Array.isArray(stand.verlauf)) {
      // Gespeicherte Antworten aus frueheren Staenden kennen noch "alter", "hoehe" (bis Runde 4) und "aussteigen"
      // (bis Runde 7): diese Schritte gibt es nicht mehr, der Filter wirft sie von selbst heraus
      this.#antworten = Object.fromEntries(Object.entries(stand.antworten || {}).filter(([schritt]) => REIHENFOLGE.includes(schritt)));
      this.#verlauf = stand.verlauf.filter((schritt) => REIHENFOLGE.includes(schritt));
      const freitexte = stand.freitexte && typeof stand.freitexte === 'object' ? stand.freitexte : { bett: stand.freitext || '' };
      this.#freitexte = Object.fromEntries(Object.entries(freitexte).filter(([schritt, wert]) => REIHENFOLGE.includes(schritt) && typeof wert === 'string').map(([schritt, wert]) => [schritt, wert.slice(0, 120)]));
      this.#offenerFreitext = ['bett', 'bisher', 'herkunft'].includes(stand.offenerFreitext) ? stand.offenerFreitext : '';
      // Den Warenkorb-Zustand liest #warenkorbPruefen() beim Anzeigen aus /cart.js - Laden fuegt nie hinzu
      if (this.#verlauf.length) {
        this.#zeige(this.#naechster(this.#verlauf[this.#verlauf.length - 1]), { fokus: false });
      } else {
        this.#zeige('bett', { fokus: false });
      }
    } else {
      this.#zeige('bett', { fokus: false });
    }
  }
}

if (!customElements.get('zw-passform')) {
  customElements.define('zw-passform', ZwPassform);
}
